/* @jsxImportSource @emotion/react */

import type { ButtonHTMLAttributes, HTMLAttributes } from 'react'
import React from 'react'
import classNames from 'classnames'

import { ChevronDownIcon, WarningIcon } from '@slite/icons'

import type { InputSize } from './constants'
import { inputSizesValues } from './constants'

export type SelectButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'size' | 'className' | 'value' | 'placeholder'
> & {
  hasError?: boolean
  isOpen?: boolean
  inputClassName?: string
  wrapperClassName?: string
  size?: InputSize
  placeholder: string
  wrapperStyle?: HTMLAttributes<HTMLDivElement>['style']
  ref?: React.Ref<HTMLButtonElement>
  hideInputMode?: boolean
}

export function SelectButton({
  hasError,
  isOpen,
  inputClassName,
  wrapperClassName,
  size = 'medium',
  style,
  placeholder,
  wrapperStyle,
  children,
  ref,
  hideInputMode,
  ...props
}: SelectButtonProps) {
  const _wrapperClassName = classNames(
    //TODO - remove margin from the components, external style should be delegated to external component
    ['border-small radius-large font-family-system'],
    {
      'border-shade-40': !hasError,
      'border-red': hasError,
    },
    props.disabled && 'background-alpha-shade-55',
    !props.disabled && 'background-transparent',
    wrapperClassName
  )

  const _inputClassName = classNames(
    [
      'flexbox flexbox-align-center flexbox-gap-8 width-full padding-horizontal-12 padding-vertical-4 background-shade-100',
      'shade-00',
      'flexbox-grow border-none',
      'radius-large placeholder-shade-20',
      'ellipsis',
    ],
    props.disabled ? 'cursor-forbidden' : 'cursor-pointer',
    {
      'focus-visible': !props.disabled && isOpen,
      'font-size-normal': size !== 'large',
      'font-size-large': size === 'large',
    },
    inputClassName
  )

  const RightIcon = hasError ? WarningIcon : ChevronDownIcon
  return (
    <label
      className={classNames(
        'position-relative flexbox flexbox-align-center',
        _wrapperClassName
      )}
      style={{
        ...(hideInputMode && {
          border: 'none',
        }),
        ...wrapperStyle,
      }}
    >
      <button
        ref={ref}
        {...props}
        className={_inputClassName}
        style={{
          minHeight: inputSizesValues[size],
          ...(hideInputMode && {
            border: 'none',
            background: 'transparent',
            padding: 0,
          }),
          ...style,
        }}
        type="button"
      >
        {children ? (
          <div className="flexbox flexbox-shrink flexbox-grow ellipsis">
            {children}
          </div>
        ) : (
          <div className="flexbox flexbox-shrink flexbox-grow ellipsis shade-20">
            {placeholder}
          </div>
        )}
        {!hideInputMode && (
          <RightIcon
            size={12}
            className="flexbox-no-shrink pointer-events-none"
            style={{
              right: 16,
              color: 'var(--shade-10)',
            }}
          />
        )}
      </button>
    </label>
  )
}
